import React from "react";
import { importedSettlementKey } from "@common/helpers/importedSettlement";
import {
    formatMatchedTransactionDate,
    MatchedTransactionRowData,
} from "@components/transactions/MatchedTransactionCard/MatchedTransactionCard";
import { AttachmentChip } from "@components/documents/FinancialDocumentAttachment/FinancialDocumentAttachment";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { Amount } from "@design-system/Amount/Amount";
import { Button } from "@design-system/Button/Button";
import { GridCol } from "@design-system/Grid/GridCol";
import { GridRow } from "@design-system/Grid/GridRow";
import { Link } from "@design-system/Link/Link";
import { Loader } from "@design-system/Loader/Loader";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { SearchIcon } from "@icons";
import {
    getSettlementLabel,
    getSettlementTooltip,
} from "./ImportedSettlementsCard";
import {
    AccrualMatchDocument,
    useAccrualMatchData,
} from "./useAccrualMatchData";
import styles from "./AccrualMatchesAttachments.module.scss";

const getMatchName = (row: MatchedTransactionRowData): string =>
    row.counterparty?.name ?? row.bankDescription;

/**
 * @purpose "Matches" row of the invoice details section, rendered as the same
 * pills as "Attachments". Shown for every entity; `AccrualDetailsPanel`
 * decides whether this row exists at all.
 */
export const AccrualMatchesAttachments: React.FC<AccrualMatchDocument> = (
    props,
) => {
    const { documentType } = props;
    const {
        workspaceId,
        hasError,
        isRetrying,
        isLoading,
        retry,
        settlements,
        rows,
        handleOpenReverseDrawer,
    } = useAccrualMatchData(props);

    if (!workspaceId) {
        return null;
    }

    let content: React.ReactNode;
    if (hasError) {
        content = (
            <div className={styles.list}>
                <p className={styles.errorValue}>Couldn&apos;t load matches</p>
                <Link size="xs" disabled={isRetrying} onClick={retry}>
                    Retry
                </Link>
            </div>
        );
    } else if (isLoading) {
        content = <Loader />;
    } else {
        content = (
            <div className={styles.list}>
                <Tooltip
                    content={`Match this ${documentType} to one or more transactions`}
                >
                    <Button
                        variant="link"
                        size="sm"
                        className={styles.addButton}
                        leftIcon={<SearchIcon />}
                        onClick={handleOpenReverseDrawer}
                    >
                        Find a match
                    </Button>
                </Tooltip>
                {rows.map((row) => {
                    const matchDate = formatMatchedTransactionDate(row);
                    return (
                        <AttachmentChip
                            key={row.key}
                            fileName={`${matchDate} ${getMatchName(row)}`}
                            label={
                                <span className={styles.pillLabel}>
                                    <span>{matchDate}</span>
                                    <EntityAvatar
                                        entity={row.entity}
                                        size="xs"
                                        aria-hidden
                                    />
                                    <Amount
                                        amount={row.amount}
                                        separateCurrencySign
                                        showNegative
                                    />
                                </span>
                            }
                            onOpen={row.onClick}
                            onDetach={() => {
                                void row.onDetach();
                            }}
                            detachLabel="Unmatch"
                            testId="accrual-match"
                        />
                    );
                })}
                {settlements.map((settlement) => {
                    const settlementLabel = getSettlementLabel({
                        settlement,
                        documentType,
                    });
                    return (
                        <Tooltip
                            key={importedSettlementKey(settlement)}
                            content={getSettlementTooltip(settlement)}
                        >
                            <div>
                                <AttachmentChip
                                    fileName={settlementLabel}
                                    label={
                                        <span className={styles.pillLabel}>
                                            <span>{settlementLabel}</span>
                                            <Amount
                                                amount={settlement.amount}
                                                separateCurrencySign
                                            />
                                        </span>
                                    }
                                    testId="accrual-imported-settlement"
                                />
                            </div>
                        </Tooltip>
                    );
                })}
            </div>
        );
    }

    return (
        <GridRow gap="0">
            <GridCol sm={4} className={styles.label}>
                Matches
            </GridCol>
            <GridCol sm={8} className={styles.value}>
                {content}
            </GridCol>
        </GridRow>
    );
};
